import { useState } from 'react'

import { BiddingApprovalState } from '@ebalance/proto/bffuser/v1/bidding_common_model_pb'
import { useTranslation } from 'react-i18next'

import { HStack, TextButton, VStack } from '@enechain/ecloud-designsystem'

import { DisclosureIcon } from '~/components/common/AccordionDisclosureIcon'
import { sortByApprovalBannerPriority } from '~/pages/SpotBidding/models/approvalBannerPriority'

/**
 * 展開せずに並べる件数 (AC-BSP-082 の見せ方 = Des 判断範囲、 2026-08-26 デザイナー判断)。
 *
 * 3 にした理由は「実運用のほぼ全部が畳まれずに見える」 ため。 同一受渡日の承認案件は
 * 典型 1〜2 件、 洗い替えを回しても 3〜4 件が現実的な上限で (spec に件数上限は無いが、
 * 1 件増やすたびに承認者の 承認 → 提出 を 1 往復する必要があるのが実質のレート制限)、
 * テスト fixture の並存も最大 2〜3 件、 8080 プロトの AC-BSP-082 デモ seed も 3 件。
 *
 * 縦幅の実測 (1440x900 / 日本語実文言): 3 件 = 329px (画面高の 37%)、 4 件 = 434px (48%)、
 * 6 件 = 648px (72%)、 9 件以上で画面から溢れる。 3 件までなら並べても収まる。
 *
 * 畳み込みは「外れ値が来ても画面が埋まらない」 ための保険であって、 既定の見せ方では
 * ない (= 畳んだ状態を常用にすると開く導線に気づけない、 2026-08-26 デザイナー指摘)。
 */
const VISIBLE_LIMIT = 3

export type ApprovalBannerEntry = {
  /** React key。 案件を一意に識別する値 (request id / group id)。 */
  id: string
  approvalState: BiddingApprovalState
  /**
   * バナー要素を作る関数。 要素ではなく関数を持たせるのは、 件数が多い時 (ブロック入札は
   * 数千グループ) に畳まれて見えない案件のバナーまで毎回作らないため。 並べ替えと畳み込みは
   * `approvalState` だけで決まるので、 表示する分 (先頭 `VISIBLE_LIMIT` 件と、 展開した時の
   * 残り) だけ呼ぶ。 バナーを出さない案件は呼び出し元が entries に入れない (render は必ず要素を返す)。
   */
  render: () => React.ReactElement
}

type Props = {
  /** 表示対象の案件。 バナーを出さない状態 (純 DRAFT 等) は呼び出し元で除外済み。 */
  entries: readonly ApprovalBannerEntry[]
}

/**
 * 承認状態バナーを優先度順に積み、 `VISIBLE_LIMIT` を超えた分だけ畳む共通部品。
 *
 * 通常入札 (`ApprovalStatusBanner`) と ブロック入札 (`BlockApprovalStatusBanner`) の
 * 両方から使う。 ブロックは 1 グループ = 1 案件でラウンドに束ねられないため、 通常入札より
 * 件数が伸びやすい (CSV 取込は 1 リクエスト 2,500 グループまで。 `spot-bid-block-csv-import.md` §5)。
 * 畳み込みが無いと画面が丸ごと埋まるので、 同じ見せ方を両方に当てる。
 */
export const ApprovalBannerStack = ({
  entries,
}: Props): React.ReactElement | null => {
  const { t } = useTranslation()
  const [isExpanded, setExpanded] = useState(false)

  if (entries.length === 0) return null

  const sorted = sortByApprovalBannerPriority(entries, (e) => e.approvalState)
  const visible = sorted.slice(0, VISIBLE_LIMIT)
  const rest = sorted.slice(VISIBLE_LIMIT)

  return (
    // `flexShrink={0}`: 呼び出し元 (SpotBidding/index.tsx) は高さ拘束された column
    // flex なので、 既定だとバナーが押し潰され、 DS `Alert` の `overflow: hidden` で
    // 中身が切り落とされる (2026-08-19 デザイナー指摘)。
    <VStack align="stretch" flexShrink={0} gap={2}>
      {visible.map((e) => (
        <VStack key={e.id} align="stretch">
          {e.render()}
        </VStack>
      ))}
      {rest.length > 0 && (
        <>
          {/* 畳んだ分の開閉導線。 **左端**に置き、 chevron を付ける
              (2026-08-26 デザイナー指摘)。
              ステータス別チップと件数は出さない — 畳まれているのが何件かは文言
              (「残り N 件を表示」) が既に伝えており、 チップを並べると読む対象が
              増えるだけで、 導線そのものが目立たなくなる。 */}
          <HStack gap={2}>
            <TextButton
              onClick={() => {
                setExpanded((v) => !v)
              }}
              size="sm"
            >
              <DisclosureIcon expanded={isExpanded} />
              {isExpanded
                ? t('page:biddingSpot.approvalBanner.collapse')
                : t('page:biddingSpot.approvalBanner.showRest', {
                    count: rest.length,
                  })}
            </TextButton>
          </HStack>
          {isExpanded && (
            <VStack align="stretch" gap={2}>
              {rest.map((e) => (
                <VStack key={e.id} align="stretch">
                  {e.render()}
                </VStack>
              ))}
            </VStack>
          )}
        </>
      )}
    </VStack>
  )
}
